// Um furo chega em dois formatos: snake_case pelo REST (GET /api/holes) e camelCase
// pelo WebSocket (hole_done). normalizeHole é o ÚNICO ponto que concilia os dois, então
// todo o resto da aplicação lê sempre o mesmo shape (camelCase).
function num(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

// Só texto e número viram string: um objeto aqui chegaria à tela como "[object Object]".
function str(...vals) {
  for (const v of vals) if ((typeof v === 'string' || typeof v === 'number') && v !== '') return String(v)
  return ''
}

// O 0.8 manda o material do furo como objeto {type, classification, grading}; o backend
// achata em colunas, mas um payload cru não pode quebrar a normalização.
const matObj = (raw) => (raw.material && typeof raw.material === 'object' ? raw.material : {})

export const HOLE_DONE = 'done'
export const HOLE_ABORTED = 'aborted'

// O status só existe a partir do 0.8; furo sem ele é concluído. O hole_done do aborto
// pelo WS também traz aborted:true, que vale mesmo sem o status.
function holeStatus(raw) {
  const s = str(raw.status).toLowerCase()
  if (s === HOLE_ABORTED || raw.aborted === true) return HOLE_ABORTED
  return HOLE_DONE
}

export function normalizeHole(raw) {
  if (!raw || typeof raw !== 'object') return null
  const status = holeStatus(raw)
  return {
    id: raw.id,
    status,
    aborted: status === HOLE_ABORTED,
    deviceName: str(raw.device_name, raw.deviceName),
    operatorName: str(raw.operator_name, raw.operatorName),
    holeId: str(raw.hole_id, raw.holeId),
    holeType: str(raw.hole_type, raw.holeType),
    material: str(raw.material),
    rockType: str(raw.rock_type, raw.rockType),
    oreGrade: str(raw.ore_grade, raw.oreGrade),
    materialType: str(raw.material_type, raw.materialType, matObj(raw).type) || null,
    materialClassification: str(raw.material_classification, raw.materialClassification, matObj(raw).classification) || null,
    materialGrading: str(raw.material_grading, raw.materialGrading, matObj(raw).grading) || null,
    characteristics: Array.isArray(raw.characteristics) ? raw.characteristics.filter(c => typeof c === 'string' && c) : [],
    depthM: num(raw.depth_m ?? raw.depthM),
    startedAt: raw.started_at ?? raw.startedAt ?? null,
    completedAt: raw.completed_at ?? raw.completedAt ?? null,
    durationSeconds: num(raw.duration_seconds ?? raw.durationSeconds),
    metersPerHour: num(raw.meters_per_hour ?? raw.metersPerHour),
    lat: num(raw.lat),
    lon: num(raw.lon),
    pitId: raw.pit_id ?? raw.pitId ?? null,
    pitName: str(raw.pit_name, raw.pitName),
    mineId: num(raw.mine_id ?? raw.mineId),
  }
}

// lat/lon vêm nulos quando o VMC gravou o furo sem fix de GPS.
export function hasPosition(hole) {
  return hole != null && hole.lat != null && hole.lon != null
}

// slugKey normaliza um rótulo (minúsculas, sem acentos, só letras e dígitos), igual ao
// slugKey da HMI (vmc-test/ui/holewizard.go): "Stérile", "Estéril" e "top soil" têm de
// colapsar na mesma chave para a comparação por palavra.
function slugKey(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, '')
}

export const MATERIAL_WASTE = 'waste'
export const MATERIAL_ORE = 'ore'
export const MATERIAL_UNKNOWN = 'unknown'

// O material é texto livre da tabela articles e o idioma da HMI segue a mina, então as
// palavras-chave cobrem os cinco idiomas (as chaves já vão normalizadas por slugKey).
// Manter em par com isWasteMaterial da HMI (vmc-test/ui/holewizard.go).
const WASTE_KEYS = ['esteril', 'waste', 'sterile', 'topsoil', 'capeamento', 'datark', 'դատարկ']
const ORE_KEYS = ['minerio', 'ore', 'minerai', 'mineral', 'հանքաքար']

// materialClass classifica o material em minério, estéril ou desconhecido. O que não bate
// com nenhuma palavra-chave fica UNKNOWN (cor neutra), nunca é assumido como minério.
export function materialClass(material) {
  const k = slugKey(material)
  if (!k) return MATERIAL_UNKNOWN
  if (WASTE_KEYS.some(w => k.includes(w))) return MATERIAL_WASTE
  if (ORE_KEYS.some(o => k.includes(o))) return MATERIAL_ORE
  return MATERIAL_UNKNOWN
}

// materialKind: no 0.8 o tipo chega como código (ORE/WASTE) e decide sozinho; o texto
// livre do 0.7 continua passando pelo classificador de palavras-chave.
export function materialKind(materialType, material) {
  const t = String(materialType || '').toUpperCase()
  if (t === 'ORE') return MATERIAL_ORE
  if (t === 'WASTE') return MATERIAL_WASTE
  return materialClass(material)
}
